"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import IntentModal from "@components/molecules/IntentModal";
import RequestCallBackModal from "@components/molecules/RequestCallBackModal";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useAppSelector } from "@lib/redux/hooks";
import type { PageType } from "@constants/pageTypes";
import type { BuyingIntentData } from "@services/globalIntentService";
import { postRequestCallback } from "@services/requestCallbackService";

type ContinueRedirect = (redirectUrl: string, intent?: string) => void;

interface IntentRedirectModalParams {
  buyingIntent?: BuyingIntentData | null;
  cityName: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  colorName?: string;
  colorHexCodes?: string[];
  pageType?: PageType;
  loginType?: string;
  continueRedirect?: ContinueRedirect;
}

interface OpenIntentOrRedirectParams {
  redirectUrl: string;
  continueRedirect?: ContinueRedirect;
}

const shouldShowIntentModal = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return sessionStorage.getItem("buying_intent") !== "false";
};

export const useIntentRedirectModal = ({
  buyingIntent,
  cityName,
  brandName = "",
  modelName = "",
  variantName = "",
  colorName = "",
  colorHexCodes = [],
  pageType,
  loginType = "Get quote",
  continueRedirect,
}: IntentRedirectModalParams) => {
  const router = useRouter();
  const userProfile = useAppSelector((state: any) => state.profile.data);
  const [isIntentModalOpen, setIsIntentModalOpen] = useState(false);
  const [isRequestCallBackModalOpen, setIsRequestCallBackModalOpen] =
    useState(false);
  const pendingRedirectUrl = useRef("");
  const pendingContinueRedirect = useRef<ContinueRedirect | undefined>(undefined);
  const isRequestCallbackSubmitting = useRef(false);

  const defaultContinueRedirect: ContinueRedirect = (redirectUrl) => {
    router.push(redirectUrl);
  };

  const continueWithRedirect = (intent?: string) => {
    const redirectUrl = pendingRedirectUrl.current;
    const redirectHandler =
      pendingContinueRedirect.current || continueRedirect || defaultContinueRedirect;

    if (!redirectUrl) {
      return;
    }

    redirectHandler(redirectUrl, intent);
  };

  const openIntentOrRedirect = ({
    redirectUrl,
    continueRedirect: overrideContinueRedirect,
  }: OpenIntentOrRedirectParams) => {
    pendingRedirectUrl.current = redirectUrl;
    pendingContinueRedirect.current = overrideContinueRedirect;

    if (!buyingIntent || !shouldShowIntentModal()) {
      const redirectHandler =
        overrideContinueRedirect || continueRedirect || defaultContinueRedirect;
      redirectHandler(redirectUrl);
      return false;
    }

    setIsIntentModalOpen(true);
    return true;
  };

  const handleGetQuoteFromIntent = async (intent?: string) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: "Intent",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_get_detailed_quote",
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_color: colorName,
        vehicle_variant: variantName,
        customer_name: userProfile?.name || "",
        email: userProfile?.email || "",
        phone: userProfile?.contact_no || userProfile?.phone || "",
        intent,
      },
    });
    setIsIntentModalOpen(false);
    continueWithRedirect(intent);
  };

  const handleRequestCallbackFromIntent = async (intent?: string) => {
    if (isRequestCallbackSubmitting.current) {
      return;
    }

    isRequestCallbackSubmitting.current = true;
    setIsIntentModalOpen(false);

    const callbackTrackingProps = {
      action_type: "tap_btn",
      action_name: "tap_btn_help",
      city_name: cityName,
      vehicle_make: brandName,
      vehicle_model: modelName,
      vehicle_color: colorName,
      vehicle_variant: variantName,
      customer_name: userProfile?.name || "",
      email: userProfile?.email || "",
      phone: userProfile?.contact_no || userProfile?.phone || "",
      intent,
    };

    try {
      const callbackResult = await postRequestCallback({
        callback_message: `Request callback for ${brandName} ${modelName} ${variantName} in ${cityName}`,
      });

      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType,
        additionalProps: callbackTrackingProps,
      });

      if (callbackResult.success) {
        setIsRequestCallBackModalOpen(true);
        return;
      }

      console.error("Request callback failed:", callbackResult.error);
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType,
        additionalProps: {
          exception_type: "API issue",
          exception_name: "failed callback request",
          ...callbackTrackingProps,
        },
      });
    } catch (error) {
      console.error("Callback API call failed:", error);
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType,
        additionalProps: {
          exception_type: "API issue",
          exception_name: "callback api call failed",
          ...callbackTrackingProps,
        },
      });
    } finally {
      isRequestCallbackSubmitting.current = false;
    }
  };

  const intentModal = (
    <>
      {isIntentModalOpen && (
        <IntentModal
          isOpen={isIntentModalOpen}
          onClose={() => setIsIntentModalOpen(false)}
          carDetails={{
            name: `${brandName} ${modelName} ${variantName}`.trim(),
            color: {
              name: colorName,
              hex: colorHexCodes,
              type: colorHexCodes.length > 1 ? "dual_tone" : "single_tone",
            },
          }}
          onRequestCallback={handleRequestCallbackFromIntent}
          onGetQuote={handleGetQuoteFromIntent}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{
            make: brandName,
            model: modelName,
            variant: variantName,
            color: colorName,
          }}
          highPriorityCapture={false}
          pageType={pageType}
          loginType={loginType}
        />
      )}

      {isRequestCallBackModalOpen && (
        <RequestCallBackModal
          isOpen={isRequestCallBackModalOpen}
          onClose={() => setIsRequestCallBackModalOpen(false)}
        />
      )}
    </>
  );

  return {
    intentModal,
    openIntentOrRedirect,
  };
};
